import { useCustomerStore } from '../stores/customer'

export function usePaymentWidget(paymentMethod: Ref<string>) {
  const customerStore = useCustomerStore()

  const router = useRouter()

  const { t, locale } = useI18n()

  useHead({
    script: [
      {
        src: useRuntimeConfig().public.nexi.script,
        type: 'text/javascript',
        tagPosition: 'bodyClose',
      },
      {
        src: useRuntimeConfig().public.nexi.bridgeJS,
        type: 'text/javascript',
        tagPosition: 'bodyClose',
      },
    ],
  })

  const isLoading = ref(false)
  const isDisabled = ref(false)
  const isDACH = ref(false)
  const isSepaVerify = ref(false)

  const errorMessage = ref('')
  const paymentWidgetRef = ref(null)
  const paymentInstrumentId = ref('')
  const paymentResult = ref<PaymentData | null>(null)
  const sepaVerifyTimestamp = ref('')
  const tinkRedirect = ref('')
  const paymentWidgetHeight = ref<string>('')
  const paymentWidgetHeights: PaymentWidgetHeights = {
    creditcard: {
      default: '25em',
      error: '27em',
    },
    sepa: {
      default: '35em',
      error: '40em',
    },
  }

  watch(paymentMethod, () => {
    setupPaymentWidget()
  })

  function setupPaymentWidget() {
    errorMessage.value = ''
    isLoading.value = false
    removePaymentWidgets()
    nextTick(() => {
      configureConcardisWidget()
      paymentWidgetHeight.value =
        paymentWidgetHeights[toValue(paymentMethod)]!.default
    })
  }

  function configureConcardisWidget() {
    window.PayEngine.setPublishableKey(
      useRuntimeConfig().public.nexi.publishableKey
    )

    const optionalParameters = {
      language: locale.value,
      products: [toValue(paymentMethod)],
      customStyleId: useRuntimeConfig().public.nexiCustomStyleId,
      hidePayButton: true,
      hideTitleIcons: true,
      skipFinalResultView: true,
      initCallback: initPaymentWidgetCallback,
      optionalFields: {
        creditCard: {
          cardHolder: true,
          cvcNeeded: true,
          cofContract: {
            type: 'UNSCHEDULED',
          },
        },
        sepa: {
          bic: true,
          bankName: false,
          requestMandate: true,
          directDebitType: 'FIRST',
        },
      },
    }

    try {
      window.PayEngineWidget.initAsInlineComponentPi(
        'paymentWidgetContainer',
        useRuntimeConfig().public.nexi.publishableKey,
        optionalParameters,
        piCreateCallback
      )
    } catch (error) {
      errorMessage.value = t('dbc-error-something-went-wrong')
      console.error('Error initializing payment widget:', error)
    }
  }

  function initPaymentWidgetCallback(error: any, result: any) {
    if (error) {
      errorMessage.value = t('dbc-error-something-went-wrong')
      return
    }
    paymentWidgetRef.value = result
  }

  function piCreateCallback(error: any, paymentInstrument: any) {
    isLoading.value = true

    if (error) {
      if (error.error === 'Iframe timeout error') {
        errorMessage.value = t(
          `dbc-error-nexi-self-service${isWebview() ? '-webview' : ''}`
        )
        removePaymentWidgets()
        paymentWidgetHeight.value = ''
      } else {
        errorMessage.value = t('dbc-error-something-went-wrong')
      }
      isLoading.value = false
      return
    }

    paymentInstrumentId.value = paymentInstrument.paymentInstrumentId
    paymentResult.value = paymentInstrument

    if (paymentInstrument.type === 'creditcard') {
      isDACH.value = true
      window.PayEngine.threeDsAuthentication(
        paymentInstrumentId.value,
        'paymentWidgetContainer',
        {
          type: 'RECURRING_CARDCHECK',
          transactionData: { amount: 0, currency: 'EUR' },
          async: {
            successUrl: 'https://www.callabike.de',
            failureUrl: 'https://www.callabike.de',
          },
        },
        threeDsAuthenticationCallback
      )
    }

    if (paymentInstrument.type === 'sepa') {
      errorMessage.value = ''
      sepaVerifyTimestamp.value = Date.now().toString()
      const iban = paymentInstrument.attributes.iban
      isDACH.value = new RegExp('^[DE | AT | CH ]{2}').test(iban)

      if (!isDACH.value) {
        onVerifySepaComplete({
          result: 'success',
          timestamp: sepaVerifyTimestamp.value,
        })
        return
      }

      fetchSepaVerify(
        locale.value,
        iban,
        paymentResult.value?.attributes.accountHolder,
        sepaVerifyTimestamp.value
      ).then((response) => {
        isLoading.value = false
        if (response.ok) {
          isSepaVerify.value = true
          tinkRedirect.value = response._data.data.redirecturl
          if (paymentResult.value) {
            paymentResult.value.zahlungsReferenzId = response._data.data._id
          }
          window.scrollTo(0, 1)
        }
        if (!response.ok) {
          errorMessage.value = t('dbc-error-something-went-wrong')
        }
      })
    }
  }

  function threeDsAuthenticationCallback(error: any, result: any) {
    if (error) {
      setupPaymentWidget()
      errorMessage.value = t('dbc-text-change-payment-error')
      return
    }

    if (result.threeDsVersion === null) {
      setupPaymentWidget()
      errorMessage.value = t('dbc-text-change-payment-error')
      return
    }

    if (result.threeDsVersion === '1.0') {
      setupPaymentWidget()
      errorMessage.value = t('dbc-text-change-payment-error')
      return
    }

    if (result.threeDsVersion === '2.0') {
      window.PayEngine.cardCheck(
        paymentInstrumentId.value,
        'UNSCHEDULED',
        null,
        result.specificData.authenticationId,
        onCardCheckCallback
      )
    }
    isLoading.value = false
  }

  function onCardCheckCallback(error: any, result: any) {
    removePaymentWidgets()
    paymentWidgetHeight.value = ''
    errorMessage.value = ''
    isLoading.value = true
    if (error) {
      setupPaymentWidget()
      errorMessage.value = t('dbc-text-change-payment-error')
      return
    }
    if (result.status === 'FAILURE') {
      setupPaymentWidget()
      errorMessage.value = t('dbc-text-change-payment-error')
      return
    }

    if (paymentResult.value) {
      paymentResult.value.threeDsAuthenticationId =
        result.threeDsData.threeDsAuthenticationId

      fetchUpdatePayment(paymentResult.value).then((response) => {
        if (response.status === 200) {
          customerStore.getContracts().then(() => {
            router.push({
              path: usePagePath('payment'),
              query: { update: 'payment-method' },
            })
            isLoading.value = false
          })
        } else {
          setupPaymentWidget()
          errorMessage.value = t('dbc-error-something-went-wrong')
        }
      })
    }
  }

  function verifySepa() {
    isDisabled.value = true
    const tinkWindow = window.open(tinkRedirect.value)

    if (
      !tinkWindow ||
      tinkWindow.closed ||
      typeof tinkWindow.closed == 'undefined'
    ) {
      errorMessage.value = t('dbc-error-something-went-wrong')
      setupPaymentWidget()
    }
  }

  async function onVerifySepaComplete(data: any) {
    if (
      data.result === 'success' &&
      data.timestamp === sepaVerifyTimestamp.value
    ) {
      isSepaVerify.value = false
      isLoading.value = true
      paymentWidgetHeight.value = ''
      removePaymentWidgets()

      const response = await fetchUpdatePayment(paymentResult.value)
      if (response.status === 200) {
        await customerStore.getContracts()
        router.push({
          path: usePagePath('payment'),
          query: { update: 'payment-method' },
        })
      } else {
        errorMessage.value = t('dbc-text-change-payment-error')
      }
      isLoading.value = false
    } else if (
      data.result === 'error' ||
      data.timestamp !== sepaVerifyTimestamp.value
    ) {
      isSepaVerify.value = false
      setupPaymentWidget()
      errorMessage.value = t('dbc-text-change-payment-error')
    }
    isDisabled.value = false
  }

  function updatePaymentData() {
    window.PayEngineWidget.validate(
      paymentWidgetRef.value,
      (error: any, result: any) => {
        if (error) {
          paymentWidgetHeight.value =
            paymentWidgetHeights[toValue(paymentMethod)]!.error
        }
        if (result) {
          window.PayEngineWidget.pay(paymentWidgetRef.value)
          paymentWidgetHeight.value =
            paymentWidgetHeights[toValue(paymentMethod)]!.default
        }
      }
    )
  }

  return {
    isDisabled,
    isLoading,
    isSepaVerify,
    errorMessage,
    paymentWidgetHeight,
    setupPaymentWidget,
    onVerifySepaComplete,
    verifySepa,
    updatePaymentData,
  }
}
